// ============================================================
// ATTACKER STATUS - Pure function to derive attacker display
// status from slot, stats, toggles, and character data.
// ============================================================
import type { Character } from './character/types';

interface ActiveBonus {
  label: string;
  source: string;
}

export interface AttackerStatus {
  ammoStatus: 'ok' | 'low' | 'empty' | 'melee';
  activeBonuses: ActiveBonus[];
  activeTalentIds: string[];
  activeTraitIds: string[];
}

type AmmoSlot = { weaponType: string; ammo?: { current: number; max: number }; isEmpty: boolean } | null;

/** A melee weapon, an empty slot, or a gun with no magazine all read as 'melee' (nothing to count). */
function ammoStatusOf(slot: AmmoSlot): AttackerStatus['ammoStatus'] {
  const ammo = slot && !slot.isEmpty && slot.weaponType !== 'melee' ? slot.ammo : undefined;
  if (!ammo || ammo.max === 0) return 'melee';
  if (ammo.current <= 0) return 'empty';
  return ammo.current <= ammo.max * 0.25 ? 'low' : 'ok';
}

/**
 * The toggle chips, in display order. Real AttackToggleState keys: the
 * originals read `toggles.aim` and `toggles.pointBlank`, which never
 * existed - so an Aim chip never showed in the Status footer, from the day
 * the chips shipped until v8.217.0.
 */
const TOGGLE_BONUSES: readonly [key: string, label: string][] = [
  ['aimHalf', 'Aim +10'],
  ['aimFull', 'Aim +20'],
  ['charge', 'Charge'],
  ['gangUp', 'Gang Up'],
  ['calledShot', 'Called Shot'],
  ['allOutAttack', 'All-Out Attack'],
  ['guardedAttack', 'Guarded'],
];

/**
 * Derive attacker display status from current combat state.
 * Pure function - no side effects.
 */
export function deriveAttackerStatus(
  slot: AmmoSlot,
  stats: { canSwiftAttack: boolean; canLightningAttack: boolean; isFrenzied: boolean } | null,
  toggles: Record<string, boolean>,
  hatred: boolean,
  character: Character | null,
  frenzied: boolean = false,
): AttackerStatus {
  const activeBonuses: ActiveBonus[] = TOGGLE_BONUSES.filter(([key]) => toggles[key]).map(([, label]) => ({
    label,
    source: 'toggle',
  }));
  if (hatred) activeBonuses.push({ label: 'Hatred', source: 'hatred' });
  if (frenzied) activeBonuses.push({ label: 'Frenzied', source: 'talent' });
  // Active talent/trait IDs from character
  const activeTalentIds: string[] = character?.talents.map((t) => t.talentId) || [];
  const activeTraitIds: string[] = character?.traits.map((t) => t.traitId) || [];
  return { ammoStatus: ammoStatusOf(slot), activeBonuses, activeTalentIds, activeTraitIds };
}
